import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const CreditCardInterestCalculator = () => {
  const content = article.find((item) => item.id === 5);
  const [balance, setBalance] = useState("5000");
  const [apr, setApr] = useState("18.99");
  const [monthlyPayment, setMonthlyPayment] = useState("200");
  const [minPayment, setMinPayment] = useState("125");
  const [minPaymentPercent, setMinPaymentPercent] = useState("2");
  const [paymentFrequency, setPaymentFrequency] = useState("Monthly");
  const [period, setPeriod] = useState("12");
  const [extraPayment, setExtraPayment] = useState("50");
  const [promoApr, setPromoApr] = useState("");
  const [promoEndDate, setPromoEndDate] = useState("");
  const [fees, setFees] = useState("0");
  const [startDate, setStartDate] = useState("");
  const [results, setResults] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const resultsRef = useRef();

  // Set default start date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
  }, []);

  // Auto-calculate when inputs change (like your first component)
  useEffect(() => {
    if (balance && apr && (monthlyPayment || minPayment || minPaymentPercent)) {
      setTimeout(() => {
        calculateInterest();
      }, 100);
    }
  }, [balance, apr, monthlyPayment, minPayment, minPaymentPercent, extraPayment, promoApr, promoEndDate, fees, period, startDate]);

  const calculateInterest = () => {
    const principal = parseFloat(balance) || 0;
    const annualRate = (parseFloat(apr) || 0) / 100;
    const promoRate = promoApr ? (parseFloat(promoApr) || 0) / 100 : null;
    const months = parseInt(period) || 12;
    const monthlyFees = parseFloat(fees) || 0;
    
    // Determine payment amount
    let payment = parseFloat(monthlyPayment) || 0;
    if (!payment && minPaymentPercent) {
      payment = principal * (parseFloat(minPaymentPercent) || 0) / 100;
    }
    if (!payment) {
      payment = parseFloat(minPayment) || 0;
    }
    
    const extra = parseFloat(extraPayment) || 0;
    const totalPayment = payment + extra;

    if (!principal || !totalPayment || (!annualRate && !promoRate)) {
      // Skip alert during initial load
      if (!results) return;
      alert("Please fill in required fields: Balance, Payment, and APR");
      return;
    }

    let remaining = principal;
    let totalInterest = 0;
    let totalPaid = 0;
    const monthlyData = [];
    const interestData = [];
    const principalData = [];
    const monthlyBreakdown = [];

    for (let i = 0; i < months && remaining > 0; i++) {
      // Check if promo APR is active - FIXED LOGIC
      const currentRate = promoRate && promoEndDate ? 
        (new Date(promoEndDate) > new Date(startDate).setMonth(new Date(startDate).getMonth() + i) ? promoRate : annualRate) 
        : (promoRate && !promoEndDate ? promoRate : annualRate);
      
      const monthlyRate = currentRate / 12;
      
      const interest = remaining * monthlyRate;
      totalInterest += interest;
      
      const appliedPayment = Math.min(totalPayment - interest, remaining);
      remaining = remaining - appliedPayment + monthlyFees;
      
      if (remaining < 0) remaining = 0;
      
      totalPaid += totalPayment + monthlyFees;
      
      monthlyData.push(remaining);
      interestData.push(interest);
      principalData.push(appliedPayment);
      
      monthlyBreakdown.push({
        month: i + 1,
        interest: interest.toFixed(2),
        principal: appliedPayment.toFixed(2),
        fees: monthlyFees.toFixed(2),
        remaining: remaining.toFixed(2),
        apr: (currentRate * 100).toFixed(2)
      });

      if (remaining <= 0) break;
    }

    // Calculate debt-free date
    const start = startDate ? new Date(startDate) : new Date();
    const debtFreeDate = new Date(start);
    const payoffMonths = monthlyBreakdown.length;
    debtFreeDate.setMonth(debtFreeDate.getMonth() + payoffMonths);

    // Calculate scenarios
    calculateScenarios(principal, annualRate, months, monthlyFees);

    setResults({
      totalInterest: totalInterest.toFixed(2),
      totalPaid: (principal + totalInterest + (monthlyFees * payoffMonths)).toFixed(2),
      remainingBalance: remaining > 0 ? remaining.toFixed(2) : "0",
      debtFree: remaining <= 0 ? `${payoffMonths} months` : `Not within ${period} months`,
      debtFreeDate: debtFreeDate.toLocaleDateString(),
      payoffMonths,
      monthlyData,
      interestData,
      principalData,
      monthlyBreakdown,
      averageMonthlyInterest: (totalInterest / Math.min(payoffMonths, months)).toFixed(2)
    });
  };

  const calculateScenarios = (principal, annualRate, months, fees) => {
    const basePayment = parseFloat(monthlyPayment) || 
                      (minPaymentPercent ? principal * (parseFloat(minPaymentPercent) || 0) / 100 : 
                      parseFloat(minPayment) || 0) + (parseFloat(extraPayment) || 0);

    const scenarioTypes = [
      { name: "Current Plan", multiplier: 1 },
      { name: "Minimum Payments", multiplier: 0.5 },
      { name: "Pay 25% More", multiplier: 1.25 },
      { name: "Double Payments", multiplier: 2 },
      { name: "Aggressive (3x)", multiplier: 3 }
    ];

    const scenarioResults = scenarioTypes.map(scenario => {
      const payment = basePayment * scenario.multiplier;
      let remaining = principal;
      let totalInterest = 0;
      let payoffMonths = 0;

      for (let i = 0; i < 600 && remaining > 0; i++) {
        const interest = remaining * (annualRate / 12);
        totalInterest += interest;
        const appliedPayment = Math.min(payment - interest, remaining);
        remaining = remaining - appliedPayment + fees;
        payoffMonths++;
        
        if (remaining <= 0) break;
      }

      return {
        name: scenario.name,
        monthlyPayment: payment.toFixed(2),
        totalInterest: totalInterest.toFixed(2),
        payoffMonths,
        totalCost: (principal + totalInterest + (fees * payoffMonths)).toFixed(2),
        multiplier: scenario.multiplier
      };
    });

    // Calculate interest savings compared to current plan
    const currentPlan = scenarioResults.find(s => s.multiplier === 1);
    scenarioResults.forEach(scenario => {
      if (scenario.multiplier !== 1 && currentPlan) {
        scenario.interestSavings = (currentPlan.totalInterest - scenario.totalInterest).toFixed(2);
      } else {
        scenario.interestSavings = "0";
      }
    });

    setScenarios(scenarioResults);
  };

  const resetCalculator = () => {
    setBalance("5000");
    setApr("18.99");
    setMonthlyPayment("200");
    setMinPayment("125");
    setMinPaymentPercent("2");
    setPaymentFrequency("Monthly");
    setPeriod("12");
    setExtraPayment("50");
    setPromoApr("");
    setPromoEndDate("");
    setFees("0");
    setResults(null);
    setScenarios(null);
    setStartDate(new Date().toISOString().split('T')[0]);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const paymentBreakdownData = {
    labels: ['Principal', 'Interest', 'Fees'],
    datasets: [
      {
        data: [
          parseFloat(balance) || 0,
          parseFloat(results?.totalInterest) || 0,
          (parseFloat(fees) || 0) * (results?.payoffMonths || 1)
        ],
        backgroundColor: ['#F06201', '#A03B00', '#FF8C42'],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const balanceOverTimeData = {
    labels: results?.monthlyData.map((_, i) => `Month ${i + 1}`) || [],
    datasets: [
      {
        label: 'Remaining Balance',
        data: results?.monthlyData || [],
        borderColor: '#F06201',
        backgroundColor: 'rgba(240, 98, 1, 0.1)',
        borderWidth: 2,
        fill: true,
      },
      {
        label: 'Interest Paid',
        data: results?.interestData || [],
        borderColor: '#A03B00',
        backgroundColor: 'rgba(160, 59, 0, 0.1)',
        borderWidth: 2,
        fill: true,
      }
    ],
  };

  const scenarioComparisonData = {
    labels: scenarios?.map(scenario => scenario.name) || [],
    datasets: [
      {
        label: 'Payoff Time (Months)',
        data: scenarios?.map(scenario => scenario.payoffMonths) || [],
        backgroundColor: '#FF8C42',
        borderColor: '#FF8C42',
        borderWidth: 1,
      },
      {
        label: 'Total Interest ($)',
        data: scenarios?.map(scenario => parseFloat(scenario.totalInterest)) || [],
        backgroundColor: '#A03B00',
        borderColor: '#A03B00',
        borderWidth: 1,
        yAxisID: 'y1',
      }
    ],
  };

  const monthlyBreakdownData = {
    labels: results?.monthlyBreakdown.slice(0, 12).map(month => `Month ${month.month}`) || [],
    datasets: [
      {
        label: 'Interest',
        data: results?.monthlyBreakdown.slice(0, 12).map(month => parseFloat(month.interest)) || [],
        backgroundColor: '#A03B00',
      },
      {
        label: 'Principal',
        data: results?.monthlyBreakdown.slice(0, 12).map(month => parseFloat(month.principal)) || [],
        backgroundColor: '#F06201',
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const scenarioChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Months'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Interest ($)'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Credit Card Interest Calculator</h1>
          <p>Calculate exact interest costs and explore different payment scenarios</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Current Balance:</span>
              <strong>${(parseFloat(balance) || 0).toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Monthly Payment:</span>
              <strong>$
                {((parseFloat(monthlyPayment) || 
                  (minPaymentPercent ? 
                    (parseFloat(balance) * parseFloat(minPaymentPercent) / 100) : 
                    parseFloat(minPayment) || 0)) + 
                  (parseFloat(extraPayment) || 0)).toFixed(2)}
              </strong>
            </div>
            <div className="summary-item">
              <span>APR:</span>
              <strong>{(parseFloat(apr) || 0).toFixed(2)}%</strong>
            </div>
            <div className="summary-item">
              <span>Analysis Period:</span>
              <strong>{period} months</strong>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Credit Card Details</h2>
              <div className="card-actions">
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}See how different payment strategies affect your total interest costs and payoff timeline
              </div>
            </div>

            <div className="cards-container">
              <div className="card-input-group active">
                <div className="card-header">
                  <h3>Credit Card Analysis</h3>
                </div>
                
                <div className="input-grid">
                  <div className="input-group">
                    <label>Current Balance ($)*</label>
                    <input
                      type="number"
                      value={balance}
                      onChange={(e) => setBalance(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>APR (%)*</label>
                    <input
                      type="number"
                      step="0.01"
                      value={apr}
                      onChange={(e) => setApr(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>Minimum Payment ($)</label>
                    <input
                      type="number"
                      value={minPayment}
                      onChange={(e) => setMinPayment(e.target.value)}
                      placeholder="Fixed amount"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>Minimum Payment (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={minPaymentPercent}
                      onChange={(e) => setMinPaymentPercent(e.target.value)}
                      placeholder="Percentage"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>Monthly Payment ($)*</label>
                    <input
                      type="number"
                      value={monthlyPayment}
                      onChange={(e) => setMonthlyPayment(e.target.value)}
                      placeholder="Your planned payment"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>Extra Payments ($)</label>
                    <input
                      type="number"
                      value={extraPayment}
                      onChange={(e) => setExtraPayment(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>Promotional APR (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={promoApr}
                      onChange={(e) => setPromoApr(e.target.value)}
                      placeholder="Optional"
                      min="0"
                      max="100"
                    />
                  </div>

                  <div className="input-group">
                    <label>Promo End Date</label>
                    <input
                      type="date"
                      value={promoEndDate}
                      onChange={(e) => setPromoEndDate(e.target.value)}
                    />
                  </div>

                  <div className="input-group">
                    <label>Monthly Fees ($)</label>
                    <input
                      type="number"
                      value={fees}
                      onChange={(e) => setFees(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="input-group">
                    <label>Analysis Period (Months)</label>
                    <input
                      type="number"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      placeholder="12"
                      min="1"
                      max="600"
                    />
                  </div>
                </div>
                <div className="required-note">* Required fields. Fill at least one payment method.</div>
              </div>
            </div>
          </div>

          <div className="payment-settings">
            <div className="section-header">
              <h2>Calculation Settings</h2>
            </div>
            <div className="settings-grid">
              <div className="input-group">
                <label>Payment Frequency</label>
                <select 
                  value={paymentFrequency} 
                  onChange={(e) => setPaymentFrequency(e.target.value)}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Bi-weekly">Bi-weekly</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </div>

              <div className="input-group">
                <label>Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
              </div>
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateInterest}>
              Calculate Interest
            </button>
          </div>

          {/* Default tips section - moved here from right panel */}
            <div className="default-results">
              <h2>Interest Analysis Results</h2>
              <div className="default-message">
                <p>Enter your credit card details and click "Calculate Interest" to see detailed cost analysis and payment scenarios.</p>
                <div className="default-tips">
                  <h3><i className="fa-solid fa-lightbulb"></i> Interest Reduction Strategies:</h3>
                  <ul>
                    <li>Even small increases in monthly payments can save hundreds in interest</li>
                    <li>Making bi-weekly payments instead of monthly can reduce interest accumulation</li>
                    <li>Focus on paying down high-APR cards first to minimize total interest</li>
                    <li>Consider balance transfer cards with 0% introductory APR offers</li>
                    <li>Always pay more than the minimum to make meaningful progress on principal</li>
                  </ul>
                </div>
              </div>
            </div>
          
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Interest Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-calendar-days"></i></div>
                  <h3>Payoff Time</h3>
                  <div className="summary-value">{results.payoffMonths} months</div>
                  <div className="summary-note">{results.debtFreeDate}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Total Interest</h3>
                  <div className="summary-value">${results.totalInterest}</div>
                  <div className="summary-note">Interest you'll pay</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
                  <h3>Total Payment</h3>
                  <div className="summary-value">${results.totalPaid}</div>
                  <div className="summary-note">Principal + Interest + Fees</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-scale-balanced"></i></div>
                  <h3>Remaining Balance</h3>
                  <div className="summary-value">${results.remainingBalance}</div>
                  <div className="summary-note">After {period} months</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Payment Breakdown</h3>
                    <Doughnut data={paymentBreakdownData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Cost Distribution:</strong> Shows how your total payment is split between principal, interest, and fees.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Balance Over Time</h3>
                    <Line data={balanceOverTimeData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Balance Progression:</strong> Track how your balance decreases and interest accumulates over time.</p>
                    </div>
                  </div>

                  {scenarios && (
                    <div className="chart-card">
                      <h3>Payment Scenario Comparison</h3>
                      <Bar data={scenarioComparisonData} options={scenarioChartOptions} />
                      <div className="chart-description">
                        <p><strong>Scenario Analysis:</strong> Compare how different payment strategies affect your payoff timeline and interest costs.</p>
                      </div>
                    </div>
                  )}

                  <div className="chart-card">
                    <h3>Monthly Payment Breakdown</h3>
                    <Bar data={monthlyBreakdownData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Monthly Analysis:</strong> See how each payment is split between interest and principal over the first year.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    <div className="result-card">
                      <h3>Current Plan Analysis</h3>
                      <div className="result-stats">
                        <div className="stat">
                          <label>Original Balance</label>
                          <span className="stat-value">${(parseFloat(balance) || 0).toFixed(2)}</span>
                        </div>
                        <div className="stat">
                          <label>Total Interest</label>
                          <span className="stat-value">${results.totalInterest}</span>
                        </div>
                        <div className="stat">
                          <label>Total Fees</label>
                          <span className="stat-value">${((parseFloat(fees) || 0) * results.payoffMonths).toFixed(2)}</span>
                        </div>
                        <div className="stat">
                          <label>Total Payment</label>
                          <span className="stat-value">${results.totalPaid}</span>
                        </div>
                        <div className="stat">
                          <label>Payoff Time</label>
                          <span className="stat-value">{results.payoffMonths} months</span>
                        </div>
                        <div className="stat">
                          <label>Debt-Free Date</label>
                          <span className="stat-value">{results.debtFreeDate}</span>
                        </div>
                        <div className="stat">
                          <label>Average Monthly Interest</label>
                          <span className="stat-value">${results.averageMonthlyInterest}</span>
                        </div>
                      </div>
                    </div>

                    {/* Monthly Breakdown */}
                    <div className="result-card">
                      <h3>Monthly Payment Breakdown</h3>
                      <div className="monthly-breakdown">
                        <div className="breakdown-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th>Interest</th>
                                <th>Principal</th>
                                <th>Fees</th>
                                <th>APR</th>
                                <th>Remaining</th>
                              </tr>
                            </thead>
                            <tbody>
                              {results.monthlyBreakdown.slice(0, 12).map((month, i) => (
                                <tr key={i}>
                                  <td>{month.month}</td>
                                  <td>${month.interest}</td>
                                  <td>${month.principal}</td>
                                  <td>${month.fees}</td>
                                  <td>{month.apr}%</td>
                                  <td>${month.remaining}</td>
                                </tr>
                              ))}
                              {results.monthlyBreakdown.length > 12 && (
                                <tr>
                                  <td colSpan="6" style={{textAlign: 'center', fontStyle: 'italic'}}>
                                    ... and {results.monthlyBreakdown.length - 12} more months
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Scenario Comparison */}
                  {scenarios && (
                    <div className="scenario-comparison">
                      <h3>Payment Scenario Analysis</h3>
                      <div className="scenario-grid">
                        {scenarios.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name}</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>Monthly Payment</label>
                                <span>${scenario.monthlyPayment}</span>
                              </div>
                              <div className="stat">
                                <label>Payoff Time</label>
                                <span>{scenario.payoffMonths} months</span>
                              </div>
                              <div className="stat">
                                <label>Total Interest</label>
                                <span>${scenario.totalInterest}</span>
                              </div>
                              <div className="stat">
                                <label>Total Cost</label>
                                <span>${scenario.totalCost}</span>
                              </div>
                              <div className="stat">
                                <label>Interest Savings</label>
                                <span className={scenario.interestSavings > 0 ? "savings" : ""}>
                                  ${scenario.interestSavings}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your details and click "Calculate Interest" to view results.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CreditCardInterestCalculator;